import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    Card,
    CardBody,
    CardHeader,
    Button,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
    Badge
} from "reactstrap";
// Replace react-datepicker with antd DatePicker
import { DatePicker } from "antd";
import dayjs from "dayjs";

import JokerStatusCard from "../../../Components/Common/JokerStatusCard";

import {
    fetchJokerSettings,
    saveJokerSettings,
    fetchJokerSessionDetails
} from "../../../slices/lt/joker/thunk";
import { clearEditMode } from "../../../slices/lt/joker/reducer";
import { formatDateTimeDDMMYYYY } from "../../../helpers/dateUtils";

import "./Joker.css";

const MULTIPLIERS = [2, 5, 10, 20, 50, 100];

const JokerSettings = () => {
    const dispatch = useDispatch();

    const {
        jokerEnabled,
        jokerNumber,
        jokerStartTime,
        jokerEndTime,
        jokerMultiplier,
        jokerId,
        loading,
        updating,
        sessionDetails,
        detailsLoading,
        currentSession
    } = useSelector((state) => state.lt.joker || {});

    const [form, setForm] = useState({
        jokerNumber: "",
        jokerStartTime: null,
        jokerEndTime: null,
        jokerMultiplier: 2
    });

    const [startTimePickerOpen, setStartTimePickerOpen] = useState(false);
    const [endTimePickerOpen, setEndTimePickerOpen] = useState(false);
    const [expandedRounds, setExpandedRounds] = useState({});

    /* ================= FETCH SETTINGS ================= */
    useEffect(() => {
        // JokerStatusCard already triggers fetchJokerSettings() on mount.
        // Keeping it here causes duplicate /api/joker?status=running requests.
    }, []);

    /* ================= FETCH SESSION DETAILS ================= */
    useEffect(() => {
        if (jokerId || currentSession?._id || currentSession?.id) {
            const id = jokerId || currentSession?._id || currentSession?.id;
            if (id) {
                dispatch(fetchJokerSessionDetails(id));
            }
        }
    }, [jokerId, currentSession?._id, currentSession?.id, dispatch]);

    /* ================= PREFILL FORM ================= */
    useEffect(() => {
        // Only populate form when jokerId exists (edit mode)
        // For create mode, keep form blank
        if (jokerId) {
            setForm(prev => ({
                ...prev,
                jokerNumber: jokerNumber ?? "",
                // Ensure we parse the time if it's a string, or null
                jokerStartTime: jokerStartTime ? new Date(jokerStartTime) : null,
                jokerEndTime: jokerEndTime ? new Date(jokerEndTime) : null,
                jokerMultiplier: jokerMultiplier ? Number(jokerMultiplier) : 2
            }));
        } else {
            // Clear form when not in edit mode (create mode)
            setForm({
                jokerNumber: "",
                jokerStartTime: null,
                jokerEndTime: null,
                jokerMultiplier: 2
            });
        }
    }, [jokerId, jokerNumber, jokerStartTime, jokerEndTime, jokerMultiplier]);

    const disabledStartDate = (current) => {
        // Can not select days before today
        return current && current < dayjs().startOf('day');
    };

    const disabledEndDate = (current) => {
        if (!form.jokerStartTime) {
            return current && current < dayjs().startOf('day');
        }
        // Can not select days before the selected start date
        return current && current < dayjs(form.jokerStartTime).startOf('day');
    };

    /* ================= HANDLERS ================= */
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleMultiplierSelect = (value) => {
        setForm({ ...form, jokerMultiplier: value });
    };

    const handleSubmit = async () => {
        // Validate form
        if (!form.jokerNumber || !form.jokerStartTime || !form.jokerEndTime) {
            toast.error("Please fill in all required fields");
            return;
        }

        try {
            const action = await dispatch(
                saveJokerSettings({
                    jokerId: jokerId, // Include jokerId for update, null for create
                    totalJokerRounds: Number(form.jokerNumber),
                    startTime: form.jokerStartTime,
                    endTime: form.jokerEndTime,
                    multiplier: form.jokerMultiplier
                })
            );

            if (saveJokerSettings.fulfilled.match(action)) {
                toast.success(jokerId ? "Joker updated successfully!" : "Joker created successfully!");
                dispatch(fetchJokerSettings()); // Refresh settings

                if (jokerId) {
                    // Update mode - clear edit mode
                    dispatch(clearEditMode());
                } else {
                    // Create mode - clear form data
                    setForm({
                        jokerNumber: "",
                        jokerStartTime: null,
                        jokerEndTime: null,
                        jokerMultiplier: 2
                    });
                    // Close time pickers if open
                    setStartTimePickerOpen(false);
                    setEndTimePickerOpen(false);
                }
            } else {
                toast.error(action.payload || (jokerId ? "Failed to update Joker" : "Failed to create Joker"));
            }
        } catch (error) {
            console.error("Error saving Joker:", error);
            toast.error("An unexpected error occurred.");
        }
    };

    const handleCancelEdit = () => {
        dispatch(clearEditMode());
        setForm({
            jokerNumber: "",
            jokerStartTime: null,
            jokerEndTime: null,
            jokerMultiplier: 2
        });
    };

    const toggleRoundDetails = (roundNumber) => {
        setExpandedRounds(prev => ({
            ...prev,
            [roundNumber]: !prev[roundNumber]
        }));
    };

    const formatDate = (dateString) => {
        return formatDateTimeDDMMYYYY(dateString);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    /* ================= UI ================= */
    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-flex align-items-center justify-content-between">
                            <h4 className="mb-0">Joker Settings</h4>
                        </div>
                    </div>
                </div>
                <Row style={{ marginTop: "30px", padding: "0px !important" }}>
                    {/* ================= LEFT SIDE ================= */}
                    <Col md={6}>
                        <Card>
                            <CardHeader>
                                <strong>{jokerId ? "Edit Joker" : "Create Joker"}</strong>
                            </CardHeader>

                            <CardBody>
                                <Form>
                                    <FormGroup>
                                        <Label>Total Joker Rounds (Frequency)</Label>
                                        <Input
                                            type="number"
                                            name="jokerNumber"
                                            min="1"
                                            value={form.jokerNumber}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="d-block">Start Time</Label>
                                                <DatePicker
                                                    disabledDate={disabledStartDate}
                                                    showTime={{ use12Hours: true, format: "hh:mm A" }}
                                                    format="DD-MM-YYYY hh:mm A"
                                                    value={form.jokerStartTime ? dayjs(form.jokerStartTime) : null}
                                                    open={startTimePickerOpen}
                                                    onOpenChange={(open) => {
                                                        setStartTimePickerOpen(open);
                                                        // Close end time picker if start time picker is opened
                                                        if (open) {
                                                            setEndTimePickerOpen(false);
                                                        }
                                                    }}
                                                    onChange={(time) => {
                                                        setForm({ ...form, jokerStartTime: time ? time.toDate() : null });
                                                    }}
                                                    onOk={(time) => {
                                                        setForm({ ...form, jokerStartTime: time ? time.toDate() : null });
                                                        setStartTimePickerOpen(false);
                                                    }}
                                                    className="w-100 joker-time-picker px-2"
                                                    placeholder="Select start date & time"
                                                    style={{ height: '38px', borderRadius: '0.25rem' }}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="d-block">End Time</Label>
                                                <DatePicker
                                                    disabledDate={disabledEndDate}
                                                    showTime={{ use12Hours: true, format: "hh:mm A" }}
                                                    format="DD-MM-YYYY hh:mm A"
                                                    value={form.jokerEndTime ? dayjs(form.jokerEndTime) : null}
                                                    open={endTimePickerOpen}
                                                    onOpenChange={(open) => {
                                                        setEndTimePickerOpen(open);
                                                        // Close start time picker if end time picker is opened
                                                        if (open) {
                                                            setStartTimePickerOpen(false);
                                                        }
                                                    }}
                                                    onChange={(time) => {
                                                        setForm({ ...form, jokerEndTime: time ? time.toDate() : null });
                                                    }}
                                                    onOk={(time) => {
                                                        setForm({ ...form, jokerEndTime: time ? time.toDate() : null });
                                                        setEndTimePickerOpen(false);
                                                    }}
                                                    className="w-100 joker-time-picker px-2"
                                                    placeholder="Select end date & time"
                                                    style={{ height: '38px', borderRadius: '0.25rem' }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <div className="d-flex justify-content-between items-end">
                                        <div>
                                            <Label className="mb-1">Joker Multiplier</Label>
                                            <div className="joker-multiplier-container">
                                                {MULTIPLIERS.map((m) => (
                                                    <button
                                                        key={m}
                                                        type="button"
                                                        className={`joker-multiplier-btn ${form.jokerMultiplier === m ? 'active' : ''}`}
                                                        onClick={() => handleMultiplierSelect(m)}
                                                    >
                                                        {m}x
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="text-end mt-4 d-flex gap-2 justify-content-end">
                                            <Button
                                                color="primary"
                                                onClick={handleSubmit}
                                                disabled={updating}
                                            >
                                                {updating ? "Saving..." : (jokerId ? "Update Joker" : "Create Joker")}
                                            </Button>
                                        </div>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* ================= RIGHT SIDE ================= */}
                    <Col md={6}>
                        <JokerStatusCard />
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default JokerSettings;
